import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Container, Typography, Box } from '@mui/material';
import { motion } from 'framer-motion';
import { blogPosts } from '../data/blogPosts';

function formatDate(iso) {
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function PostRow({ post, index }) {
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, delay: 0.1 + index * 0.08, ease: [0.22, 1, 0.36, 1] }}
    >
      <Box
        component={RouterLink}
        to={`/blog/${post.id}`}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: { xs: 3, md: 5 },
          textDecoration: 'none',
          py: { xs: 4, md: 5 },
          borderTop: '1px solid rgba(255,255,255,0.09)',
          transition: 'border-color 0.3s ease',
          '&:hover': { borderTopColor: 'rgba(255,255,255,0.2)' },
        }}
      >
        {/* Index number */}
        <Typography sx={{
          fontFamily: '"SF Mono", ui-monospace, Menlo, Consolas, monospace',
          fontSize: '0.78rem',
          color: hovered ? 'rgba(245,245,247,0.55)' : 'rgba(245,245,247,0.28)',
          pt: '0.4em',
          flexShrink: 0,
          transition: 'color 0.25s ease',
          display: { xs: 'none', sm: 'block' },
        }}>
          {String(index + 1).padStart(2, '0')}
        </Typography>

        {/* Text */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4, mb: 1.4, flexWrap: 'wrap' }}>
            <Typography sx={{
              fontFamily: '"SF Mono", ui-monospace, Menlo, Consolas, monospace',
              fontSize: '0.74rem',
              color: 'rgba(245,245,247,0.4)',
            }}>
              {formatDate(post.date)}
            </Typography>
            {post.tags.slice(0, 1).map((tag) => (
              <Box key={tag} sx={{ display: 'flex', alignItems: 'center', gap: 1.4 }}>
                <Box sx={{ width: '3px', height: '3px', borderRadius: '50%', background: 'rgba(245,245,247,0.25)' }} />
                <Typography sx={{
                  fontFamily: '"SF Mono", ui-monospace, Menlo, Consolas, monospace',
                  fontSize: '0.74rem',
                  color: 'rgba(245,245,247,0.4)',
                }}>
                  {tag}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography sx={{
              fontFamily: '"Bricolage Grotesque", -apple-system, BlinkMacSystemFont, sans-serif',
              fontWeight: 600,
              fontSize: 'clamp(1.4rem, 3.2vw, 2rem)',
              letterSpacing: '-0.035em',
              lineHeight: 1.12,
              color: hovered ? '#F5F5F7' : 'rgba(245,245,247,0.82)',
              transition: 'color 0.25s ease',
            }}>
              {post.title}
            </Typography>
            <Box
              component="span"
              sx={{
                fontSize: '1.3rem',
                color: 'rgba(245,245,247,0.7)',
                opacity: hovered ? 1 : 0,
                transform: hovered ? 'translateX(0)' : 'translateX(-6px)',
                transition: 'opacity 0.25s ease, transform 0.25s ease',
                flexShrink: 0,
              }}
            >
              &#8599;
            </Box>
          </Box>

          <Typography sx={{
            color: 'rgba(245,245,247,0.5)',
            fontSize: '0.98rem',
            lineHeight: 1.65,
            mt: 1.4,
            maxWidth: 560,
          }}>
            {post.excerpt}
          </Typography>
        </Box>

        {/* Thumbnail */}
        {post.coverImage && (
          <Box sx={{
            display: { xs: 'none', md: 'block' },
            width: 168,
            height: 116,
            flexShrink: 0,
            borderRadius: '8px',
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.08)',
          }}>
            <Box sx={{
              width: '100%',
              height: '100%',
              background: `url(${post.coverImage}) center/cover no-repeat`,
              transition: 'transform 0.5s cubic-bezier(0.22,1,0.36,1)',
              transform: hovered ? 'scale(1.05)' : 'scale(1)',
            }} />
          </Box>
        )}
      </Box>
    </motion.div>
  );
}

export default function Blog() {
  return (
    <Container maxWidth="md" sx={{ mt: { xs: 10, md: 16 }, mb: 16 }}>

      {/* Masthead */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <Box sx={{ mb: { xs: 6, md: 9 } }}>
          <Typography sx={{
            fontFamily: '"Bricolage Grotesque", -apple-system, BlinkMacSystemFont, sans-serif',
            fontWeight: 700,
            fontSize: 'clamp(2.6rem, 8vw, 4.5rem)',
            letterSpacing: '-0.045em',
            lineHeight: 1,
            color: '#F5F5F7',
            mb: 2.5,
          }}>
            Writing
          </Typography>
          <Typography sx={{
            color: 'rgba(245,245,247,0.5)',
            fontSize: '1.05rem',
            lineHeight: 1.6,
            maxWidth: 460,
          }}>
            Notes on engineering, the projects I build, and lessons picked up along the way.
          </Typography>
        </Box>
      </motion.div>

      {/* Index */}
      {blogPosts.length === 0 ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
          <Box sx={{
            py: 12,
            borderTop: '1px solid rgba(255,255,255,0.09)',
          }}>
            <Typography sx={{ color: 'rgba(245,245,247,0.35)', fontSize: '0.95rem' }}>
              No posts yet. Add one in{' '}
              <Box component="code" sx={{ color: 'rgba(245,245,247,0.6)', fontFamily: '"SF Mono", ui-monospace, monospace' }}>
                src/data/blogPosts.js
              </Box>
            </Typography>
          </Box>
        </motion.div>
      ) : (
        <Box sx={{ borderBottom: '1px solid rgba(255,255,255,0.09)' }}>
          {blogPosts.map((post, i) => (
            <PostRow post={post} index={i} key={post.id} />
          ))}
        </Box>
      )}
    </Container>
  );
}
